import { create } from 'zustand';
import { apiClient } from '@/services/api';

export interface FileNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  children?: FileNode[];
  language?: string;
  size?: number;
}

interface FileTreeState {
  tree: FileNode[];
  expandedPaths: Set<string>;
  loading: boolean;

  // Actions
  loadFiles: (projectId: string) => Promise<void>;
  toggleExpand: (path: string) => void;
  refreshFile: (projectId: string, path: string) => Promise<void>;
}

function buildTree(files: Array<{ path: string; language?: string; size?: number }>): FileNode[] {
  const root: FileNode[] = [];
  const dirs = new Map<string, FileNode>();

  // Sort files for consistent ordering
  const sorted = [...files].sort((a, b) => a.path.localeCompare(b.path));

  for (const file of sorted) {
    const parts = file.path.split('/');
    let currentPath = '';

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const parentPath = currentPath;
      currentPath = currentPath ? `${currentPath}/${part}` : part;

      if (i === parts.length - 1) {
        // File node
        const node: FileNode = {
          name: part,
          path: file.path,
          type: 'file',
          language: file.language || undefined,
          size: file.size,
        };

        if (parentPath && dirs.has(parentPath)) {
          dirs.get(parentPath)!.children!.push(node);
        } else {
          root.push(node);
        }
      } else {
        // Directory node
        if (!dirs.has(currentPath)) {
          const dirNode: FileNode = {
            name: part,
            path: currentPath,
            type: 'directory',
            children: [],
          };
          dirs.set(currentPath, dirNode);

          if (parentPath && dirs.has(parentPath)) {
            dirs.get(parentPath)!.children!.push(dirNode);
          } else {
            root.push(dirNode);
          }
        }
      }
    }
  }

  return root;
}

export const useFileTreeStore = create<FileTreeState>((set) => ({
  tree: [],
  expandedPaths: new Set<string>(),
  loading: false,

  loadFiles: async (projectId) => {
    set({ loading: true });
    try {
      const files = await apiClient.getProjectFiles(projectId);
      const tree = buildTree(files);
      // Auto-expand top-level directories
      const topLevelDirs = tree
        .filter((node) => node.type === 'directory')
        .map((node) => node.path);
      set({ tree, loading: false, expandedPaths: new Set(topLevelDirs) });
    } catch {
      set({ loading: false });
    }
  },

  toggleExpand: (path) => {
    set((state) => {
      const next = new Set(state.expandedPaths);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return { expandedPaths: next };
    });
  },

  refreshFile: async (projectId, _path) => {
    // Reload the full tree — could be optimized for single file later
    const files = await apiClient.getProjectFiles(projectId);
    const tree = buildTree(files);
    set({ tree });
  },
}));
